import React, { useState, useEffect } from "react";
import { fetchVehicleData } from "../api/flespiApi";
import "./VehiclesList.css";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const VehiclesList = ({ vehicles, onVehicleSelection }) => {
  const [vehicleData, setVehicleData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [expandedVehicles, setExpandedVehicles] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const vehiclesPerPage = 10;
  const [movingCount, setMovingCount] = useState(0);
  const [stoppedCount, setStoppedCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);

  // Get the logged-in user (if needed for other logic)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // No state update needed unless used elsewhere
    });
    return () => unsubscribe();
  }, []);

  // Track selection
  useEffect(() => {
    onVehicleSelection(selectedVehicles);
  }, [selectedVehicles, onVehicleSelection]);

  // Pull live data from Flespi with polling
  useEffect(() => {
    const fetchAllVehicleData = async () => {
      const dataPromises = vehicles.map(async (vehicle) => {
        try {
          const data = await fetchVehicleData(vehicle.deviceId);
          return { id: vehicle.id, data };
        } catch (error) {
          console.error(`Error fetching vehicle data for ${vehicle.id}:`, error);
          return { id: vehicle.id, data: null };
        }
      });

      const resolvedData = await Promise.all(dataPromises);
      const newVehicleData = {};
      let moving = 0,
        stopped = 0,
        offline = 0;

      const now = new Date().getTime();
      const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

      resolvedData.forEach(({ id, data }) => {
        newVehicleData[id] = data;
        if (data) {
          const lastUpdate = new Date(data.lastUpdateTime).getTime();
          const speed = data.speed || 0;
          if (lastUpdate < twentyFourHoursAgo) {
            offline++;
          } else if (speed > 0) {
            moving++;
          } else {
            stopped++;
          }
        }
      });

      setVehicleData(newVehicleData);
      setMovingCount(moving);
      setStoppedCount(stopped);
      setOfflineCount(offline);
    };

    if (vehicles.length > 0) {
      fetchAllVehicleData();
      const interval = setInterval(fetchAllVehicleData, 30000);
      return () => clearInterval(interval);
    }
  }, [vehicles]);

  const toggleVehicleSelection = (vehicleId) => {
    setSelectedVehicles((prev) =>
      prev.includes(vehicleId)
        ? prev.filter((id) => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const toggleExpand = (vehicleId) => {
    setExpandedVehicles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  const filteredVehicles = vehicles
    .filter((vehicle) =>
      vehicle.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(
      (vehicle) =>
        statusFilter === "all" ||
        vehicleData[vehicle.id]?.movement?.toLowerCase() ===
          statusFilter.toLowerCase()
    );

  const indexOfLastVehicle = currentPage * vehiclesPerPage;
  const indexOfFirstVehicle = indexOfLastVehicle - vehiclesPerPage;
  const currentVehicles = filteredVehicles.slice(
    indexOfFirstVehicle,
    indexOfLastVehicle
  );

  return (
    <div className="vehicle-list">
      <input
        type="text"
        placeholder="Search vehicles..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-bar"
      />
      <div className="status-filters">
        <button
          className={`moving ${statusFilter === "Moving" ? "active" : ""}`}
          onClick={() =>
            setStatusFilter(statusFilter === "Moving" ? "all" : "Moving")
          }
        >
          Moving <span className="count">{movingCount}</span>
        </button>
        <button
          className={`stopped ${statusFilter === "Stopped" ? "active" : ""}`}
          onClick={() =>
            setStatusFilter(statusFilter === "Stopped" ? "all" : "Stopped")
          }
        >
          Stopped <span className="count">{stoppedCount}</span>
        </button>
        <button
          className={`offline ${statusFilter === "Offline" ? "active" : ""}`}
          onClick={() =>
            setStatusFilter(statusFilter === "Offline" ? "all" : "Offline")
          }
        >
          Offline <span className="count">{offlineCount}</span>
        </button>
      </div>
      <div className="total-count">
        <strong>Total Vehicles:</strong> {vehicles.length}
      </div>
      {currentVehicles.length === 0 ? (
        <p>No vehicles found...</p>
      ) : (
        currentVehicles.map((vehicle) => {
          const vehicleInfo = vehicleData[vehicle.id] || {};
          return (
            <div key={vehicle.id} className="vehicle-item">
              <div
                className="vehicle-header"
                onClick={() => toggleExpand(vehicle.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedVehicles.includes(vehicle.id)}
                  onChange={() => toggleVehicleSelection(vehicle.id)}
                />
                <strong>{vehicle.plateNumber || "Unknown Vehicle"}</strong>
                <span
                  className={`vehicle-speed ${
                    vehicleInfo.speed > 0 ? "moving" : "stopped"
                  }`}
                >
                  {vehicleInfo.speed || 0} km/h
                </span>
                <span className="last-update">
                  🕒 {vehicleInfo.lastUpdateTime || "N/A"}
                </span>
                {expandedVehicles.has(vehicle.id) ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </div>
              {expandedVehicles.has(vehicle.id) && (
                <div className="vehicle-details">
                  <table>
                    <tbody>
                      <tr>
                        <td>📍 Location:</td>
                        <td>{vehicleInfo.location || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>🔑 IMEI:</td>
                        <td>{vehicleInfo.imei || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>🚗 Speed:</td>
                        <td>{vehicleInfo.speed || "0"} km/h</td>
                      </tr>
                      <tr>
                        <td>🔄 Movement:</td>
                        <td>{vehicleInfo.movement || "Unknown"}</td>
                      </tr>
                      <tr>
                        <td>⚡ External Battery:</td>
                        <td>{vehicleInfo.externalBattery || "N/A"}V</td>
                      </tr>
                      <tr>
                        <td>🔋 Internal Battery:</td>
                        <td>{vehicleInfo.internalBattery || "N/A"}%</td>
                      </tr>
                      <tr>
                        <td>🔋 SOC:</td>
                        <td>{vehicleInfo.soc || "N/A"}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
      <div className="pagination">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>Page {currentPage}</span>
        <button
          onClick={() => setCurrentPage((prev) => prev + 1)}
          disabled={indexOfLastVehicle >= filteredVehicles.length}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default VehiclesList;